/**
 * Brand-research SSE pipeline → store subscriber.
 *
 * Translates `brand:*` typed events into department-store mutations.
 * Mirror of the legacy `SSECallbacks` ladder that used to live in
 * `pipelines/brandSSE.ts:handleEvent`. Migrated in Phase 8 commit 72.
 *
 * Notes on shared state:
 *   - `awaitingStrategyConfirmation` is read straight from the store
 *     (`campaignData.brandStrategyAwaitingConfirmation`) — no local
 *     copy needed; the store is the single source of truth.
 *   - The pipeline's `pipelineCompleted` flag drives the
 *     `brand:stream-ended-incomplete` event from `onStreamEnd`.
 *     Subscribers don't need to track it locally.
 */

import { sseEvents } from "./sseEvents";
import { toThoughtEvent } from "../store/department/thoughtStream";
import type {
  CampaignData,
  CoAgentMessage,
  StepStatus,
  ThoughtEvent,
} from "../store/department/types";
import { BRAND_LOGO_VARIANT_COUNT } from "@/config/brandLogo";
import { trackBrandStepCompleted } from "@/shared/lib/gtm";

interface BrandSubscriberHooks {
  addStepLog: (stepIndex: number, text: string) => void;
  addThoughtEvent: (stepIndex: number, event: Omit<ThoughtEvent, "id" | "ts">) => void;
  updateCampaignData: (data: Partial<CampaignData>) => void;
  addMessage: (msg: Omit<CoAgentMessage, "id" | "timestamp">) => void;
  setStepState: (
    updater: (state: {
      currentStepIndex: number;
      stepStatuses: Record<number, StepStatus>;
    }) => Partial<{ currentStepIndex: number; stepStatuses: Record<number, StepStatus> }>,
  ) => void;
  setCampaignName: (name: string) => void;
  setFocusedStep: (index: number) => void;
  /** Reads `campaignData.brandStrategyAwaitingConfirmation` — the gating
   *  flag for `brand_generation` and `logo_generation` events. */
  isAwaitingStrategyConfirmation: () => boolean;
  /**
   * Returns `'refresh'` when the run was kicked off from the brand-detail
   * page so wizard-only side effects (step state, focus, chat, session
   * naming, step log/thought writes) can be suppressed.
   */
  getResearchMode: () => "create" | "refresh";
}

let wired = false;

const NOOP = () => {};

/**
 * Wrap raw hooks so refresh-mode runs (kicked off from
 * `/brands/:id`) suppress wizard-only side effects: step state /
 * focus / step log / chat messages / session naming.
 *
 * `updateCampaignData` and `isAwaitingStrategyConfirmation` are
 * always live — research progress and the per-source distillation
 * results need to land in the store regardless of the source flow,
 * so the brand-detail page can render them.
 */
function refreshAwareHooks(raw: BrandSubscriberHooks): BrandSubscriberHooks {
  const isRefresh = raw.getResearchMode() === "refresh";
  if (!isRefresh) return raw;
  return {
    ...raw,
    addStepLog: NOOP,
    addThoughtEvent: NOOP,
    addMessage: NOOP,
    setStepState: NOOP,
    setCampaignName: NOOP,
    setFocusedStep: NOOP,
  };
}

export function wireBrandSubscriber(
  getRawHooks: () => BrandSubscriberHooks,
): void {
  if (wired) return;
  wired = true;
  const getHooks = () => refreshAwareHooks(getRawHooks());

  // Push a thought event from the brand copywriter (silent if no copy).
  const thought = (
    step: number,
    phase: string,
    status: string,
    data?: Record<string, unknown>,
    progress?: Record<string, unknown>,
  ) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const ev = toThoughtEvent("brand", phase, status, data as any, progress as any);
    if (ev) getHooks().addThoughtEvent(step, ev);
  };

  sseEvents.subscribe("brand:session-named", ({ name }) => {
    getHooks().setCampaignName(name);
  });

  sseEvents.subscribe("brand:website-scrape-running", ({ url, data }) => {
    const hooks = getHooks();
    hooks.updateCampaignData({
      brandResearchPhase: "website_scrape",
      brandResearchProgress: { phase: "website_scrape", completed: 0, total: 1 },
    });
    hooks.addStepLog(1, `Scraping website: ${url}...`);
    thought(1, "website_scrape", "running", data);
  });

  sseEvents.subscribe("brand:website-scrape-complete", ({ data }) => {
    const hooks = getHooks();
    const chars = Number(data.chars ?? 0);
    const title = typeof data.title === "string" ? data.title : undefined;
    const url = typeof data.url === "string" ? data.url : "";
    hooks.addStepLog(1, `[SUCCESS] Scraped ${chars} chars from ${title || url}`);
    thought(1, "website_scrape", "complete", data);
    hooks.updateCampaignData({
      brandResearchPhase: "query_generation",
      brandResearchProgress: { phase: "website_scrape", completed: 1, total: 1 },
      ...(data.content
        ? { brandWebsiteContent: data.content as CampaignData["brandWebsiteContent"] }
        : {}),
    });
  });

  sseEvents.subscribe("brand:website-scrape-skipped", ({ reason, data }) => {
    const hooks = getHooks();
    hooks.addStepLog(1, `[WARN] Website scrape skipped: ${reason}`);
    thought(1, "website_scrape", "skipped", data);
    hooks.updateCampaignData({
      brandResearchPhase: "query_generation",
      brandResearchProgress: { phase: "website_scrape", completed: 1, total: 1 },
    });
  });

  sseEvents.subscribe("brand:query-generation-running", ({ data }) => {
    const hooks = getHooks();
    hooks.updateCampaignData({
      brandResearchPhase: "query_generation",
      brandResearchProgress: { phase: "query_generation", completed: 0, total: 1 },
    });
    thought(1, "query_generation", "running", data);
  });

  sseEvents.subscribe("brand:query-generation-complete", ({ queryCount, data }) => {
    const hooks = getHooks();
    hooks.addStepLog(1, `[SUCCESS] Generated ${queryCount} brand research queries`);
    thought(1, "query_generation", "complete", data);
    hooks.updateCampaignData({
      brandResearchPhase: "collection",
      brandResearchProgress: {
        phase: "query_generation",
        completed: queryCount,
        total: queryCount,
      },
      ...(data.query_plan
        ? { brandQueryPlan: data.query_plan as CampaignData["brandQueryPlan"] }
        : {}),
    });
  });

  sseEvents.subscribe("brand:collection-running", ({ data, progress }) => {
    const hooks = getHooks();
    const trackCounts = progress?.track_counts as Record<string, number> | undefined;
    const trackTotals = progress?.track_totals as Record<string, number> | undefined;
    const perTrack =
      trackCounts && trackTotals
        ? Object.fromEntries(
            ["twitter", "perplexity", "youtube", "google_search"].map((k) => [
              k,
              { completed: trackCounts[k] ?? 0, total: trackTotals[k] ?? 0 },
            ]),
          )
        : undefined;
    hooks.updateCampaignData({
      brandResearchPhase: "collection",
      brandResearchProgress: {
        phase: (progress?.track as string | undefined) || "collection",
        completed: Number(progress?.completed ?? 0),
        total: Number(progress?.total ?? 0),
      },
      ...(perTrack
        ? {
            brandCollectionTrackProgress:
              perTrack as CampaignData["brandCollectionTrackProgress"],
          }
        : {}),
    });
    thought(1, "collection", "running", data, progress);
  });

  sseEvents.subscribe("brand:collection-complete", ({ data }) => {
    const hooks = getHooks();
    const successful = Number(data.successful ?? 0);
    const failed = Number(data.failed ?? 0);
    const durationMs = Number(data.duration_ms ?? 0);
    hooks.addStepLog(
      1,
      `[SUCCESS] Collected ${successful} results (${failed} failed) in ${Math.round(durationMs / 1000)}s`,
    );
    thought(1, "collection", "complete", data);
    hooks.updateCampaignData({
      brandResearchPhase: "distillation",
      brandResearchProgress: {
        phase: "collection",
        completed: successful + failed,
        total: successful + failed,
      },
    });
  });

  sseEvents.subscribe("brand:distillation-running", ({ data }) => {
    const hooks = getHooks();
    hooks.updateCampaignData({
      brandResearchPhase: "distillation",
      brandResearchProgress: { phase: "distillation", completed: 0, total: 1 },
    });
    thought(1, "distillation", "running", data);
  });

  sseEvents.subscribe("brand:distillation-complete", ({ data }) => {
    const hooks = getHooks();
    const findingsCount = Number(data.findings_count ?? 0);
    const sections = Number(data.sections ?? 0);
    hooks.addStepLog(
      1,
      `[SUCCESS] Distilled ${findingsCount} findings across ${sections} sections`,
    );
    thought(1, "distillation", "complete", data);
    hooks.updateCampaignData({
      brandResearchPhase: "synthesis",
      brandResearchProgress: { phase: "distillation", completed: 1, total: 1 },
      brandTwitterResult: data.twitter as CampaignData["brandTwitterResult"],
      brandPerplexityResult: data.perplexity as CampaignData["brandPerplexityResult"],
      brandYoutubeResult: data.youtube as CampaignData["brandYoutubeResult"],
      brandGoogleSearchResult: data.google_search as CampaignData["brandGoogleSearchResult"],
    });
    hooks.setFocusedStep(1); // Research insights now visible in Market Research widget
  });

  sseEvents.subscribe("brand:synthesis-running", ({ data }) => {
    const hooks = getHooks();
    hooks.updateCampaignData({
      brandResearchPhase: "synthesis",
      brandResearchProgress: { phase: "synthesis", completed: 0, total: 1 },
    });
    thought(1, "synthesis", "running", data);
  });

  sseEvents.subscribe("brand:synthesis-complete", ({ data }) => {
    const hooks = getHooks();
    trackBrandStepCompleted("market_research", {
      has_synthesis: Boolean(data.synthesis),
    });
    hooks.addStepLog(1, "[SUCCESS] Brand strategy synthesized");
    thought(1, "synthesis", "complete", data);
    hooks.updateCampaignData({
      brandResearchPhase: "synthesis",
      brandResearchProgress: { phase: "synthesis", completed: 1, total: 1 },
      brandSynthesis: data.synthesis as CampaignData["brandSynthesis"],
      brandStrategyAwaitingConfirmation: true,
      brandPendingPopulateResult: undefined,
    });
    hooks.setStepState((state) => ({
      currentStepIndex: 2,
      stepStatuses: { ...state.stepStatuses, 1: "completed", 2: "waiting" },
    }));
    hooks.setFocusedStep(2);
    hooks.addStepLog(2, "Review the strategy, then confirm to continue");
    hooks.addThoughtEvent(2, {
      kind: "waiting",
      headline: "Your turn to review",
      detail: "Check the brand strategy and confirm to continue",
    });
  });

  sseEvents.subscribe("brand:brand-generation-running", ({ data }) => {
    const hooks = getHooks();
    if (hooks.isAwaitingStrategyConfirmation()) return;
    hooks.updateCampaignData({
      brandResearchPhase: "brand_generation",
      brandResearchProgress: { phase: "brand_generation", completed: 0, total: 1 },
    });
    thought(2, "brand_generation", "running", data);
  });

  sseEvents.subscribe("brand:brand-generation-complete", ({ data }) => {
    const hooks = getHooks();
    if (hooks.isAwaitingStrategyConfirmation()) {
      hooks.updateCampaignData({
        brandPendingPopulateResult: data.populate_result as CampaignData["brandPendingPopulateResult"],
        brandResearchPhase: "brand_generation",
        brandResearchProgress: { phase: "brand_generation", completed: 1, total: 1 },
      });
      hooks.addStepLog(2, "Brand identity is ready — confirm to continue");
      hooks.addThoughtEvent(2, {
        kind: "waiting",
        headline: "Brand identity ready",
        detail: "Confirm to continue into visual design",
      });
      return;
    }
    trackBrandStepCompleted("brand_strategy", {
      has_populate_result: Boolean(data.populate_result),
    });
    hooks.addStepLog(2, "[SUCCESS] Brand config generated");
    thought(2, "brand_generation", "complete", data);
    // brandConfig is auto-rebuilt from brandPopulateResult by updateCampaignData
    hooks.updateCampaignData({
      brandResearchPhase: "logo_generation",
      brandResearchProgress: { phase: "brand_generation", completed: 1, total: 1 },
      brandPopulateResult: data.populate_result as CampaignData["brandPopulateResult"],
    });
    hooks.setStepState((state) => ({
      currentStepIndex: 3,
      stepStatuses: { ...state.stepStatuses, 2: "completed", 3: "waiting" },
    }));
    hooks.setFocusedStep(3);
  });

  sseEvents.subscribe("brand:logo-generation-idle", ({ data }) => {
    const hooks = getHooks();
    if (hooks.isAwaitingStrategyConfirmation()) return;
    trackBrandStepCompleted("brand_identity", {
      logo_variants_expected: BRAND_LOGO_VARIANT_COUNT,
    });
    hooks.updateCampaignData({
      brandResearchPhase: "logo_generation",
      brandResearchProgress: {
        phase: "logo_generation",
        completed: 0,
        total: BRAND_LOGO_VARIANT_COUNT,
      },
      brandLogoGenerationStatus: "idle",
    });
    hooks.setStepState((state) => ({
      currentStepIndex: 4,
      stepStatuses: { ...state.stepStatuses, 3: "completed", 4: "waiting" },
    }));
    hooks.setFocusedStep(4);
    hooks.addStepLog(
      4,
      `Logo generation ready. Generate ${BRAND_LOGO_VARIANT_COUNT} AI logo variants when ready.`,
    );
    thought(4, "logo_generation", "idle", data);
  });

  sseEvents.subscribe("brand:done", ({ data }) => {
    const hooks = getHooks();
    if (hooks.isAwaitingStrategyConfirmation()) {
      hooks.updateCampaignData({
        brandResearchStatus: "complete",
        brandResearchPhase: "synthesis",
        brandResearchProgress: { phase: "synthesis", completed: 1, total: 1 },
        brandTwitterResult: data.twitter as CampaignData["brandTwitterResult"],
        brandPerplexityResult: data.perplexity as CampaignData["brandPerplexityResult"],
        brandYoutubeResult: data.youtube as CampaignData["brandYoutubeResult"],
        brandGoogleSearchResult: data.google_search as CampaignData["brandGoogleSearchResult"],
        brandSynthesis: data.synthesis as CampaignData["brandSynthesis"],
        ...(data.populate_result
          ? {
              brandPendingPopulateResult:
                data.populate_result as CampaignData["brandPendingPopulateResult"],
            }
          : {}),
      });
      return;
    }
    // brandConfig is auto-rebuilt from the merged research data by updateCampaignData
    hooks.updateCampaignData({
      brandResearchStatus: "complete",
      brandResearchPhase: "done",
      brandResearchProgress: { phase: "done", completed: 1, total: 1 },
      brandStrategyAwaitingConfirmation: false,
      brandPendingPopulateResult: undefined,
      brandTwitterResult: data.twitter as CampaignData["brandTwitterResult"],
      brandPerplexityResult: data.perplexity as CampaignData["brandPerplexityResult"],
      brandYoutubeResult: data.youtube as CampaignData["brandYoutubeResult"],
      brandGoogleSearchResult: data.google_search as CampaignData["brandGoogleSearchResult"],
      brandSynthesis: data.synthesis as CampaignData["brandSynthesis"],
      brandPopulateResult: data.populate_result as CampaignData["brandPopulateResult"],
    });
    hooks.setStepState((state) => ({
      currentStepIndex: 4,
      stepStatuses: {
        ...state.stepStatuses,
        3: "completed",
        4: state.stepStatuses[4] === "completed" ? "completed" : "waiting",
      },
    }));
    thought(4, "done", "complete", data);
    hooks.addMessage({
      role: "agent",
      content: `Brand research complete! Here are your insights across Twitter/X, market intelligence, YouTube, and Google Search:`,
      widget: "brandResearchProgress",
    });
  });

  sseEvents.subscribe("brand:error", ({ message }) => {
    const hooks = getHooks();
    hooks.updateCampaignData({
      brandResearchStatus: "error",
      brandResearchPhase: "error",
      brandStrategyAwaitingConfirmation: false,
      brandPendingPopulateResult: undefined,
    });
    hooks.addStepLog(1, `[ERROR] ${message}`);
    thought(1, "error", "error", { message });
    hooks.setStepState((state) => ({
      currentStepIndex: 0,
      stepStatuses: { ...state.stepStatuses, 0: "waiting", 1: "pending" },
    }));
    hooks.addMessage({
      role: "agent",
      content: `Brand research encountered an error: ${message}`,
    });
  });

  sseEvents.subscribe("brand:stream-ended-incomplete", () => {
    const hooks = getHooks();
    hooks.updateCampaignData({
      brandResearchStatus: "error",
      brandResearchPhase: "error",
      brandStrategyAwaitingConfirmation: false,
      brandPendingPopulateResult: undefined,
    });
    hooks.addStepLog(1, "[ERROR] Research connection closed unexpectedly. Please try again.");
    hooks.addThoughtEvent(1, {
      kind: "error",
      headline: "Connection lost",
      detail: "Research disconnected — please try again",
    });
    hooks.setStepState((state) => ({
      currentStepIndex: 0,
      stepStatuses: { ...state.stepStatuses, 0: "waiting", 1: "pending" },
    }));
    hooks.addMessage({
      role: "agent",
      content: "Brand research disconnected before completing. Please try again.",
    });
  });
}
